'use client'
import React, { useEffect, useMemo, useRef } from 'react'
import { MenuItem, TextField } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { disAgency } from 'src/views/abi/fields/options'
import { disDocumentType } from 'src/views/abi/fields/disOptions'

const AGENCY_ALIAS_TO_DOC_CODES = {
  USDA: ['AMS', 'APH', 'FAS'],
  FSIS: ['FSI'],
  CPSC: ['CPS'],
  NHTSA: ['NHT'],
  DDTC: ['DTC']
}

const normalizeAgencyCode = (agencyValue) => {
  if (!agencyValue) return ''
  const normalized = String(agencyValue).trim()

  // Supports values like "FWS" and "FWS - Fish and Wildlife Service"
  return normalized.includes(' - ')
    ? normalized.split(' - ')[0].trim().toUpperCase()
    : normalized.toUpperCase()
}

const getDocCodesForAgency = (agencyValue) => {
  const normalizedCode = normalizeAgencyCode(agencyValue)
  if (!normalizedCode) return []

  return AGENCY_ALIAS_TO_DOC_CODES[normalizedCode] || [normalizedCode]
}

const DisUploadForm = ({ formData, setFormData }) => {
  const prevAgencyRef = useRef(formData?.agency)
  const availableDocCodeSet = useMemo(
    () => new Set(disDocumentType.map((opt) => opt.code)),
    []
  )
  const availableDisAgency = useMemo(
    () =>
      disAgency.filter((option) =>
        getDocCodesForAgency(option.value).some((code) =>
          availableDocCodeSet.has(code)
        )
      ),
    [availableDocCodeSet]
  )

  // Reset documentType when agency changes
  useEffect(() => {
    if (prevAgencyRef.current !== formData?.agency) {
      prevAgencyRef.current = formData?.agency
      setFormData((prev) => ({ ...prev, documentType: '' }))
    }
  }, [formData?.agency, setFormData])

  // Clear agency if it no longer has document type support.
  useEffect(() => {
    if (!formData?.agency) return

    const selectedAgencyCode = normalizeAgencyCode(formData.agency)
    const isAvailable = availableDisAgency.some(
      (option) => normalizeAgencyCode(option.value) === selectedAgencyCode
    )

    if (!isAvailable) {
      setFormData((prev) => ({ ...prev, agency: '', documentType: '' }))
    }
  }, [formData?.agency, availableDisAgency, setFormData])

    const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? ''
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Filter document types by selected agency (supports aliases and label-style values)
  const selectedAgencyCode = normalizeAgencyCode(formData?.agency)
  const targetDocCodes = getDocCodesForAgency(formData?.agency)
  const filteredDocTypes = selectedAgencyCode
    ? disDocumentType.filter((opt) => targetDocCodes.includes(opt.code))
    : []

  return (
    <Grid container spacing={2.5}>
      <Grid xs={12} sm={6}>
                <TextField
          select
          fullWidth
          name='agency'
          label='Agency'
          placeholder='Please select Agency'
          value={formData?.agency || ''}
          onChange={handleChange('agency')}
          required
          size='small'
        >
          {availableDisAgency.map((option) => (
            <MenuItem value={option.value} key={option.value}>
              {option.value} - {option.description}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid xs={12} sm={6}>
                <TextField
          select
          fullWidth
          name='documentType'
          label='Document Type'
          placeholder='Please select Document Type'
          value={formData?.documentType || ''}
          onChange={handleChange('documentType')}
          required
          disabled={!formData?.agency}
          size='small'
        >
          {filteredDocTypes.map((option) => (
            <MenuItem value={option.value} key={option.value} sx={{ whiteSpace: 'normal' }}>
              {option.description}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid xs={12} sm={6}>
                <TextField
          fullWidth
          name='actionIdentificationNumber'
          label='Action Identification Number'
          value={formData?.actionIdentificationNumber || ''}
          onChange={handleChange('actionIdentificationNumber')}
          size='small'
        />
      </Grid>
      <Grid xs={12} sm={6}>
                <TextField
          fullWidth
          name='comment'
          label='Comment'
          value={formData?.comment || ''}
          onChange={handleChange('comment')}
          size='small'
        />
      </Grid>
    </Grid>
  )
}

export default DisUploadForm
